import { eq, inArray } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@bso/powersync";

/**
 * ── Local-first writes that CANNOT be lost silently ───────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and still return "success"**. Measured in `space-academy-app` (same
 * mechanism, `activity` table): a course is created, 1,03 s later its
 * first unit is created and **there is no `course/updated` operation in between**—the
 * author's rename never generated a CRUD operation and the student's
 * certificate was sealed with the old title. Also confirmed in the canonical
 * `listto-app` (`9ae347b6`): 17 statements with no protection.
 *
 * The mechanism: when the CRUD queue (`ps_crud`) drains, PowerSync
 * **rebuilds the local view from the last checkpoint**. If the checkpoint
 * does not yet contain the newly created row, the row is **ABSENT** during that
 * instante — un UPDATE contra ella no escribe nada, no genera op y **no
 * fails**. The window is not "X ms elapsed": it is "the queue drained midway", so
 * it cannot be detected by looking at timings afterward.
 *
 * In bso this affects highlights, notes, bookmarks, and reading progress—
 * the user creates the row and edits/completes/undoes it in the SAME session, before
 * the checkpoint can confirm the INSERT. The absence of an error
 * proves nothing here: the server write path intentionally responds 2xx
 * (a 4xx would block the client queue forever).
 *
 * This module's contract: write, **READ BACK**, and compare against the
 * exact patch requested. If it did not land, retry with backoff;
 * if it is still missing after exhausting backoff, **THROW**—the caller communicates it
 * instead of leaving the user looking at a change that was not saved.
 *
 * ── Deliberate difference from the canonical system (`listto-app`) ───────────
 * There, `patchRowVerified` generated `updated_at` internally on each attempt
 * (excluded from comparison because it changes every pass) because
 * listto mutations did not share a `now` across sibling statements. In
 * bso each handler already calculates ONE `now` and reuses it across several statements in the
 * same gesture (e.g. deleting a highlight seals the flat row and its source group
 * with the SAME instant). Therefore, here the `patch` owns ALL of its
 * fields, including `updated_at`/`deleted_at`: every retry resends the same
 * value (idempotent), and verification compares the complete patch as-is. No
 * special case is needed for the timestamp.
 *
 * ── What this module does NOT cover (deliberately) ──────────────────────────
 * Hard `DELETE`s (physically deleting children when replacing tags/verses of
 * a note or highlight, and `hardDeleteNote` from undo of "crear") are not
 * verified: the natural check would be "the row is no longer there", which is
 * exactly the ambiguous state in this window—you cannot distinguish "it was
 * deleted" from "it had not arrived yet". Verifying them would require another signal (the CRUD
 * CRUD queue), not a reread.
 *
 * The canonical equivalent of `waitForRowPresent` is also unnecessary (for
 * mutations inside a transaction, where retrying without sleeping with the lock
 * held is impossible): in bso no write to these tables runs
 * inside a `db.transaction()`—each INSERT/UPDATE is a standalone statement,
 * so retrying outside is always possible without that problem. If in the
 * future a mutation combines INSERT+UPDATE inside a transaction, that case
 * will indeed need to port `waitForRowPresent` from the canonical system.
 */

/**
 * Retry backoff. Total ≈ 6,05 s: comfortably covers a checkpoint round trip
 * without leaving a quick user action (delete, undo,
 * mark) hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly ids: readonly string[],
    readonly fields: readonly string[],
  ) {
    super(
      `Local write did not land on ${ids.length === 1 ? `row ${ids[0]}` : `rows [${ids.join(", ")}]`} ` +
        `(fields: ${fields.join(", ")}). The row is not in PowerSync view or the value was not saved.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** `null` and `undefined` are the same "empty" value when compared against SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

const patchLanded = (
  row: Record<string, unknown> | undefined,
  patch: Record<string, unknown>,
): boolean =>
  row !== undefined && Object.entries(patch).every(([key, value]) => sameValue(row[key], value));

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, reads back, compares ALL
 * patch fields, and retries with backoff until they land. Throws
 * `LocalWriteLostError` if they are still missing after backoff is exhausted.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db.update(table).set(patch).where(eq(idColumn, id));

    // `table` is generic (`SQLiteTable`, without concrete columns), so
    // Drizzle cannot type the returned row any further: it is the same
    // function for `bible_note`, `bible_bookmark`, etc. The actual shape (a
    // column→value record) is known only to the mutation itself.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const rows = (await db
      .select()
      .from(table)
      .where(eq(idColumn, id))
      .limit(1)) as unknown as Record<string, unknown>[];

    if (patchLanded(rows[0], patch)) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError([id], Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Like {@link patchRowVerified} but for the `UPDATE … WHERE id IN (…)`
 * used by batch logical deletes/restores (a group of
 * highlighted verses, several rows of one day's progress). Retries the
 * SAME complete statement (idempotent: repeating the patch on rows that already
 * have it changes nothing) until EVERY id in the batch has landed.
 */
export async function patchRowsVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  ids: readonly string[],
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  if (ids.length === 0) {
    return;
  }
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set(patch)
      .where(inArray(idColumn, [...ids]));

    // Ver nota equivalente en `patchRowVerified`.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const rows = (await db
      .select()
      .from(table)
      .where(inArray(idColumn, [...ids]))) as unknown as Record<string, unknown>[];
    const byId = new Map(rows.map((row) => [row[idColumn.name], row]));
    const missing = ids.filter((id) => !patchLanded(byId.get(id), patch));

    if (missing.length === 0) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(missing, Object.keys(patch));
    }
    await sleep(wait);
  }
}
